import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, of, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { LoginRequest, LoginResponse, PermissionArea, User, UserRole } from './auth.type';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';

  private currentUserSubject = new BehaviorSubject<User | null>(this.getUserFromStorage());
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(
      `${environment.apiBaseURL}/auth/login`,
      credentials
    ).pipe(
      tap(response => {
        this.setToken(response.token);
        this.setUser(response.user);
        this.currentUserSubject.next(response.user);
      })
    );
  }

  refreshCurrentUser(): Observable<User | null> {
    if (!this.isAuthenticated()) {
      return of(null);
    }
    return this.http.get<User>(`${environment.apiBaseURL}/auth/me`).pipe(
      tap(user => {
        this.setUser(user);
        this.currentUserSubject.next(user);
      }),
      catchError(() => of(this.getCurrentUser()))
    );
  }

  logout(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.currentUserSubject.next(null);
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /** Returns true if the current user has the given role. */
  hasRole(role: UserRole): boolean {
    return this.getCurrentUser()?.roles?.includes(role) ?? false;
  }

  /** Returns true if the current user has at least one of the given roles. */
  hasAnyRole(...roles: UserRole[]): boolean {
    const userRoles = this.getCurrentUser()?.roles ?? [];
    return roles.some(r => userRoles.includes(r));
  }

  /** Permissions only restrict Admins; SuperAdmins and non-admin roles are never limited by them. */
  canRead(area: PermissionArea): boolean {
    return this.isUnrestricted() || !!this.getCurrentUser()?.permissions?.[area];
  }

  canWrite(area: PermissionArea): boolean {
    return this.isUnrestricted() || this.getCurrentUser()?.permissions?.[area] === 'write';
  }

  private isUnrestricted(): boolean {
    return this.hasRole('SuperAdmin') || !this.hasRole('Admin');
  }

  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  private setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  private getUserFromStorage(): User | null {
    const userJson = localStorage.getItem(this.USER_KEY);
    return userJson ? JSON.parse(userJson) : null;
  }
}
